import { Search, Loader2, AlertCircle } from 'lucide-react'
import { useState } from 'react'

interface TraceLookupProps {
    onLookup: (traceId: string) => Promise<void>
    isLoading: boolean
    error: string | null
}

export function TraceLookup({ onLookup, isLoading, error }: TraceLookupProps) {
    const [traceId, setTraceId] = useState('')

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (traceId.trim()) {
            onLookup(traceId.trim())
        }
    }

    // Basic UUID validation regex for UI feedback
    const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(traceId)
    const showWarning = traceId.length > 0 && !isValidUUID

    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Trace Lookup</h2>
            <p className="text-sm text-gray-400 mb-4">
                Enter the exact Trace ID (UUID) to inspect the execution tree.
            </p>

            <form onSubmit={handleSubmit} className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                    type="text"
                    value={traceId}
                    onChange={(e) => setTraceId(e.target.value)}
                    placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
                    className="w-full pl-12 pr-4 py-4 bg-black/50 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 font-mono text-sm shadow-inner"
                    disabled={isLoading}
                />

                <button
                    type="submit"
                    disabled={isLoading || !traceId}
                    className="absolute right-2 top-2 bottom-2 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Inspect'}
                </button>
            </form>

            {showWarning && (
                <div className="mt-2 flex items-center gap-2 text-yellow-500 text-xs">
                    <AlertCircle className="w-3 h-3" />
                    <span>Invalid UUID format</span>
                </div>
            )}

            {error && (
                <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-sm">Lookup Failed</h4>
                        <p className="text-xs mt-1 opacity-90">{error}</p>
                    </div>
                </div>
            )}
        </div>
    )
}
